import { Tag } from "./ui";
import { formatRelative } from "../lib/format";
import { money } from "../lib/money";
import type { QuotaRequest } from "../lib/types";

/**
 * What an operator looks at before deciding a request, in one line: how old
 * the organization is, whether it has ever paid, whether it is suspended,
 * what it holds of the resource today against the ask, and how its earlier
 * requests went. The same line on the queue row and inside the decision
 * modal, so the modal never asks for a fact the row already showed.
 *
 * "Never paid" and "suspended" are the two that decide most requests on
 * their own, which is why they are tags and the rest is text.
 */
export const RESOURCE_LABEL = {
  apps: "apps",
  databases: "databases",
  tunnels: "private connections",
  domains: "domain names",
  custom_domains: "custom hostnames",
} as const;

export function QuotaContext({ request }: { request: QuotaRequest }) {
  const org = request.organization;
  const age = formatRelative(org.createdAt).replace(" ago", "");
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-fg-subtle">
      <span>{age} old</span>
      <span>·</span>
      <span>{money(org.creditMicros)} credit</span>
      {org.paid ? <Tag tone="good">has paid</Tag> : <Tag tone="warn">never paid</Tag>}
      {org.suspended && <Tag tone="bad">suspended</Tag>}
      <span>·</span>
      <span>
        holds {request.held} of {request.currentLimit} {RESOURCE_LABEL[request.resource]}
      </span>
      {(request.prior.approved > 0 || request.prior.denied > 0) && (
        <>
          <span>·</span>
          <span>
            before: {request.prior.approved} approved, {request.prior.denied} denied
          </span>
        </>
      )}
    </div>
  );
}
